import { measures } from '../services/stats/calcStats';
import { ISOtoMicroseconds, convertPoint, defaultValue, computeDefaultSpeed } from '../helpers';

const targetSceneTime = 4;
const maxCalculatedSpeed = 128;

// accepts a scene object and bootstraps defaults which may not have been defined on server
class Scene {
  constructor(scene, options = {}) {
    this.rawJson = { ...scene };
    this.title = defaultValue(scene.title, '');
    this.shareImg = defaultValue(scene.shareImg, '');
    this.loading = defaultValue(scene.loading, {});
    this.stats = this.computeStats(scene.stats);
    this.defaultStatsVisible = defaultValue(scene.defaultStatsVisible, 1);
    this.live = defaultValue(scene.live, false);
    this.devMode = defaultValue(scene.devMode, false);
    this.accountType = defaultValue(scene.accountType, undefined);
    this.mapSource = defaultValue(scene.mapSource, { name: 'BingMaps' });
    this.errors = [];
    this.startPoint = this.computePoint(scene, 'startPoint');
    this.endPoint = defaultValue(this.computePoint(scene, 'endPoint'), null);
    this.muxerEndpoint = defaultValue(scene.muxerEndpoint, undefined);
    this.sceneSettings = {
      enableLighting: defaultValue(scene.enableLighting, false),
      speed: defaultValue(
        scene.defaultSpeed,
        Math.min(maxCalculatedSpeed, computeDefaultSpeed(this.startPoint, this.endPoint, targetSceneTime)),
      ),
      targetDistance: defaultValue(scene.defaultTargetDistance, 2500),
      autoplay: defaultValue(scene.autoplay, false),
      hideElements: defaultValue(scene.hideElements, []).concat(options.hideElements),
      reverseHeaderLayout: defaultValue(scene.reverseHeaderLayout, false),
      trailLength: defaultValue(scene.trailLength, 1000000),
      targetPitch: defaultValue(scene.defaultCameraPitch, undefined),
    };

    this.activities = {};
    if (scene.activities) {
      scene.activities.forEach(a => {
        this.addActivity(a);
      });
    }
    this.exitScreen = defaultValue(scene.exitScreen, []);
    this.numberOfItemsAwaitingReady = 0;
    this.media = defaultValue(scene.media, []);
    this.distanceMarkers = defaultValue(scene.distanceMarkers, undefined);

    this.segments = scene.segments ? scene.segments.slice(0) : [];
    this.setupSegments();
    this.integrations = defaultValue(scene.integrations, []);
    this.course = scene.course;
  }

  addActivity(a) {
    console.assert(
      !this.activities[a.activityId],
      'Duplicate activity detected in Scene! We need to add better support for AccountId/ActivityId references',
    );
    this.activities[a.activityId] = a;
  }

  // Computes the start OR end point. Must specify the type as "endPoint" or "startPoint"
  computePoint(scene, type) {
    let point = null;
    if (scene[type]) {
      point = convertPoint(scene[type]);
    }

    let activities = defaultValue(scene.activities, []);
    if (scene.course && scene.course.activity) {
      activities = activities.concat(scene.course.activity);
    }

    if (!scene[type] && activities.length > 0) {
      point = activities
        .filter(a => a[type])
        .map(a => convertPoint(a[type]))
        .reduce((a, b) => {
          if (type === 'startPoint') {
            return a.time < b.time ? a : b;
          } else {
            return a.time > b.time ? a : b;
          }
        }, false);
    }

    if (!point && type === 'startPoint') this.errors.push(`no ${type}`);
    return point;
  }

  computeStats(statsList = []) {
    return statsList.filter(s => {
      if (!measures[s]) {
        console.warn(`Ignoring invalid stat "${s}"`);
      }
      return !!measures[s];
    });
  }

  setupSegments() {
    // convert each existing segment to radians
    this.segments.forEach(s => {
      s.time = ISOtoMicroseconds(s.time);
      if (s.points) {
        s.points = s.points.map(p => convertPoint(p));
      }
      return s;
    });
  }
}

export { Scene as default };
